import { jobDefinition } from 'utils/jobDefinition';
import { v4 as uuidv4 } from 'uuid';

import type {
  JobArgs,
  JobField,
  JobInput,
  JobQueue,
  JobSpec,
  JobValues,
} from './types';

/**
 * Turn a job spec and a set of form values into the exact request conchBatch
 * accepts, plus the command line that reproduces it.
 *
 * Pure on purpose. The preview pane and the submit path call this with the same
 * arguments, so what an operator reads before clicking is what is sent — the
 * one property that makes a payload preview worth having.
 */

/**
 * vCPU per queue, mirroring conchBatch's own QUEUE_RESOURCES. Duplicated rather
 * than fetched because it is needed to render a field maximum before any
 * request is made, and it changes about once a year.
 */
export const QUEUE_VCPU: Record<JobQueue, number> = {
  'gg-queue-efs-8xlarge': 32,
  'gg-queue-efs-4xlarge': 16,
};

/** conchBatch's container override when it does not recognise the queue. */
const FALLBACK_VCPU = 8;

/**
 * One core is left for the parent process. Over-subscribing is not rejected by
 * Batch — the fit just crawls — so the ceiling has to be applied here.
 */
export const maxWorkers = (queue: string): number =>
  (QUEUE_VCPU[queue as JobQueue] ?? FALLBACK_VCPU) - 1;

export const clampWorkers = (workers: number, queue: string): number => {
  const ceiling = maxWorkers(queue);
  if (!Number.isFinite(workers) || workers < 1) return ceiling;
  return Math.min(Math.floor(workers), ceiling);
};

/**
 * `YYYY-MM-DD` in the operator's own timezone.
 *
 * Not `toISOString()`: that converts to UTC first, so any evening west of
 * Greenwich reports tomorrow's date, and any morning east of it reports
 * yesterday's. Either one silently trains on the wrong review cut.
 */
export const localDate = (date: Date = new Date()): string =>
  date.toLocaleDateString('en-CA');

/**
 * Suffix that makes a version id unique within a day.
 *
 * Six hex characters, matching the ids already in perception_model_job
 * (`v-2026-09-03-f04408`). The form reissues this whenever the inputs change
 * and after a launch, so the previewed id is always the submitted id.
 */
export const createRunNonce = (): string => uuidv4().slice(0, 6);

/**
 * Version ids are keyed on the review cut, not on the clock: two runs of the
 * same pull date belong to the same data, and the suffix separates the attempts.
 */
export const versionId = (pullDate: string, nonce: string): string =>
  `v-${pullDate}-${nonce}`;

/**
 * conchBatch rejects anything else, and the R scripts parse with
 * `strsplit(arg, "=")[[2]]`, so a bare flag raises "subscript out of bounds"
 * and an '=' in a value is silently truncated. Checked here so a bad value is
 * a message under the field rather than a 400 after a round trip.
 */
const SCRIPT_ARG_PATTERN = /^--[A-Za-z_][A-Za-z0-9_]*=[A-Za-z0-9._:/-]+$/;

export const formatArgs = (args: JobArgs): string[] =>
  Object.entries(args).map(([key, value]) => `--${key}=${value}`);

/** The flags that conchBatch would reject, so the form can say which and why. */
export const invalidArgs = (args: string[]): string[] =>
  args.filter((arg) => !SCRIPT_ARG_PATTERN.test(arg));

/** A number field's ceiling, which may depend on the other values. */
export const resolveMax = (
  field: JobField,
  values: JobValues,
): number | undefined => {
  if (field.type !== 'number' || field.max === undefined) return undefined;
  return typeof field.max === 'function' ? field.max(values) : field.max;
};

/**
 * Hold every number field inside its bounds.
 *
 * Called whenever a value changes, because a ceiling can move: dropping from a
 * 32-core queue to a 16-core one has to take 31 workers down with it, or the
 * form shows a number the run will not use.
 */
export const clampValues = (spec: JobSpec, values: JobValues): JobValues => {
  const next = { ...values };
  spec.fields.forEach((field) => {
    if (field.type !== 'number') return;
    const current = Number(next[field.name]);
    if (!Number.isFinite(current)) return;
    const max = resolveMax(field, next);
    if (max !== undefined && current > max) next[field.name] = max;
    if (field.min !== undefined && current < field.min) {
      next[field.name] = field.min;
    }
  });
  return next;
};

/**
 * The queue is hardware and is sent as `job_queue`; it is never a script flag.
 * The job definition is inherited from the environment rather than picked, so a
 * dev console cannot dispatch onto production compute.
 */
export const buildJobInput = (
  spec: JobSpec,
  values: JobValues,
  environment: SensoryLinkEnvironment | null,
  nonce: string,
): JobInput => ({
  script_path: spec.scriptPath,
  args: formatArgs(spec.buildArgs(values, nonce)),
  // null rather than a guess: an unknown environment defaults to the production
  // definition in `jobDefinition`, and submitting that from a console that does
  // not know where it is running is the one outcome worth blocking.
  job_definition: environment ? jobDefinition(environment) : null,
  job_queue: (spec.queueField
    ? values[spec.queueField]
    : undefined) as JobQueue,
});

/** The equivalent command line, for reproducing a run locally. */
export const cliCommand = (input: JobInput): string =>
  [`Rscript ${input.script_path}`, ...input.args].join(' \\\n  ');

/**
 * Read one flag back out of a built input.
 *
 * The version id in particular is needed after the fact — it is what the run
 * history is matched on — and reparsing the flags keeps a single source for it
 * rather than having callers recompute it and drift.
 */
export const argValue = (input: JobInput, name: string): string | undefined =>
  input.args
    .find((arg) => arg.startsWith(`--${name}=`))
    ?.slice(name.length + 3);
